import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ShoppingCart,
  AccessTime,
  ErrorOutline,
  TrendingUp,
  Refresh,
  CheckCircle
} from '@mui/icons-material';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area
} from 'recharts';
import axios from 'axios';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [statusDistribution, setStatusDistribution] = useState([]);
  const [averageDeliveryTime, setAverageDeliveryTime] = useState(null);
  const [problemOrders, setProblemOrders] = useState([]);
  const [orderVolume, setOrderVolume] = useState([]);
  const [error, setError] = useState(null);
  const [fulfillmentCenter, setFulfillmentCenter] = useState('');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = fulfillmentCenter ? { fulfillment_center: fulfillmentCenter } : {};
      
      const [statusRes, deliveryRes, problemRes, volumeRes] = await Promise.all([
        axios.get('/api/orders/dashboard/status-distribution', { params }),
        axios.get('/api/orders/dashboard/average-delivery-time', { params }),
        axios.get('/api/orders/dashboard/problem-orders', { params }),
        axios.get('/api/orders/dashboard/order-volume', { params })
      ]);

      setStatusDistribution(statusRes.data.data.distribution);
      setAverageDeliveryTime(deliveryRes.data.data);
      setProblemOrders(problemRes.data.data);
      setOrderVolume(volumeRes.data.data);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fulfillmentCenter]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'sent': return '#6b7280'; // Gray
      case 'fulfilled': return '#3b82f6'; // Blue
      case 'delivered': return '#22c55e'; // Green
      case 'paid': return '#22c55e'; // Green
      case 'hold': return '#fb923c'; // Orange
      case 'problem': return '#ef4444'; // Red
      case 'cancelled': return '#8b5cf6'; // Purple
      default: return '#6b7280';
    }
  };



  const StatCard = ({ title, value, icon, color, description, unit = '' }) => (
    <Card className="glass-card" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: color, mr: 2 }}>
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ color: '#ffffff', fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" component="div" sx={{ mb: 1, fontWeight: 'bold', color: '#ffffff' }}>
          {value}{unit}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center', color: '#ffffff' }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={fetchDashboardData}
          startIcon={<Refresh />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#ffffff', textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>
          Fulfillment Analytics
          {fulfillmentCenter && (
            <Chip 
              label={fulfillmentCenter === 'eliz' ? 'EliZ' : 'MetaKocka'} 
              size="small" 
              sx={{ 
                ml: 2, 
                backgroundColor: fulfillmentCenter === 'eliz' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                color: '#ffffff',
                border: `1px solid ${fulfillmentCenter === 'eliz' ? '#3b82f6' : '#22c55e'}`,
                fontSize: '0.75rem'
              }} 
            />
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl 
            variant="outlined" 
            size="small"
            sx={{ 
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255,255,255,0.7)',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#ffffff',
              },
              '& .MuiSelect-icon': {
                color: '#ffffff',
              },
            }}
          >
            <InputLabel>Fulfillment Center</InputLabel>
            <Select
              value={fulfillmentCenter}
              onChange={(e) => setFulfillmentCenter(e.target.value)}
              label="Fulfillment Center"
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }
                }
              }}
            >
              <MenuItem value="">All Centers</MenuItem>
              <MenuItem value="eliz">EliZ</MenuItem>
              <MenuItem value="metakocka">MetaKocka</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
            className="glass-button"
            sx={{ 
              color: '#ffffff',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={statusDistribution.reduce((sum, item) => sum + item.count, 0)}
            icon={<ShoppingCart fontSize="large" />}
            color="#3b82f6"
            description="All orders in system"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Delivery"
            value={averageDeliveryTime?.averageDeliveryTimeDays || 0}
            unit=" days"
            icon={<AccessTime fontSize="large" />}
            color="#22c55e"
            description={`Based on ${averageDeliveryTime?.sampleSize || 0} orders`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Problem Orders"
            value={problemOrders.length}
            icon={<ErrorOutline fontSize="large" />}
            color="#ef4444"
            description="Requiring attention"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Last 30 Days"
            value={orderVolume.reduce((sum, day) => sum + day.total, 0)}
            icon={<TrendingUp fontSize="large" />}
            color="#f59e0b"
            description="Order volume"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Status Distribution Pie Chart */}
        <Grid item xs={12} md={5}>
          <Card className="glass-card">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                Order Status Distribution
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {statusDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusDistribution.map(item => ({
                          ...item,
                          fill: getStatusColor(item.status)
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        label={({ status, percentage }) => `${status}: ${percentage}%`}
                      >
                        {statusDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0,0,0,0.8)', 
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>No data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Volume Over Time */}
        <Grid item xs={12} md={7}>
          <Card className="glass-card">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                Order Volume Trends (Last 30 Days)
              </Typography>
              <Box sx={{ height: 300 }}>
                {orderVolume.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={orderVolume}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#ffffff', fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fill: '#ffffff', fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0,0,0,0.8)', 
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          backdropFilter: 'blur(10px)'
                        }}
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="delivered" 
                        stackId="1"
                        stroke="#22c55e" 
                        fill="#22c55e" 
                        fillOpacity={0.7}
                        name="Delivered"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="fulfilled" 
                        stackId="1"
                        stroke="#3b82f6" 
                        fill="#3b82f6" 
                        fillOpacity={0.7}
                        name="Fulfilled"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="sent" 
                        stackId="1"
                        stroke="#6b7280" 
                        fill="#6b7280" 
                        fillOpacity={0.7}
                        name="Sent"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Problem Orders Table */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card className="glass-card">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                Recent Problem Orders (Last 10)
              </Typography>
              {problemOrders.length > 0 ? (
                <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Order #</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Tracking</TableCell>
                        <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {problemOrders.map((order) => (
                        <TableRow key={order.id} sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                          <TableCell sx={{ color: '#ffffff' }}>
                            #{order.shopify_order_number}
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff' }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.customer_name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {order.customer_email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff' }}>
                            €{order.total_amount || '0.00'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(order.status),
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff' }}>
                            {order.external_tracking_number || 'No tracking'}
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff' }}>
                            {new Date(order.updated_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircle sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                    No Problem Orders! 🎉
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    All orders are processing smoothly
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 